import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import * as XLSX from 'xlsx';
import Layout from '../../components/layout/Layout';
import accountsApi from '../../services/accountsApi';
import { Download, ListChecks } from 'lucide-react';
import toast from 'react-hot-toast';

const DIMENSIONS = [
  { key: 'institution', label: 'Institution' },
  { key: 'phase', label: 'Phase' },
  { key: 'district', label: 'District' },
];

const STATUS_OPTIONS = [
  { key: '', label: 'All' },
  { key: 'fully_paid', label: 'Fully Paid' },
  { key: 'partially_paid', label: 'Partially Paid' },
  { key: 'unpaid', label: 'Not Yet Paid' },
];

// Read-only drill-down into one institution's installment schedule —
// Institutions.js has the full add/edit version of this; here it's just
// "what does this row's schedule look like."
const InstallmentDrilldown = ({ institution, onClose }) => {
  const [installments, setInstallments] = useState(null);

  useEffect(() => {
    accountsApi.get(`/institutions/${institution.id}/installments`)
      .then(res => setInstallments(res.data.installments))
      .catch(() => toast.error('Failed to load installments'));
  }, [institution.id]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col">
        <div className="bg-blue-900 text-white p-4 rounded-t-xl flex justify-between items-center flex-shrink-0">
          <h2 className="text-lg font-bold flex items-center gap-2"><ListChecks size={18} /> {institution.code} — Installments</h2>
          <button onClick={onClose} className="text-white text-2xl font-bold">×</button>
        </div>
        <div className="p-6 overflow-y-auto">
          {installments === null ? (
            <div className="text-center py-6 text-gray-500 text-sm">Loading...</div>
          ) : installments.length === 0 ? (
            <div className="text-center py-6 text-gray-500 text-sm">No installment schedule defined for this institution yet</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-center px-3 py-2 text-gray-600 font-semibold">#</th>
                  <th className="text-right px-3 py-2 text-gray-600 font-semibold">Amount</th>
                  <th className="text-left px-3 py-2 text-gray-600 font-semibold">Due Date</th>
                  <th className="text-center px-3 py-2 text-gray-600 font-semibold">Status</th>
                  <th className="text-left px-3 py-2 text-gray-600 font-semibold">Voucher</th>
                </tr>
              </thead>
              <tbody>
                {installments.map(inst => (
                  <tr key={inst.id} className="border-t border-gray-100">
                    <td className="px-3 py-2 text-center text-gray-700">{inst.installment_number}</td>
                    <td className="px-3 py-2 text-right text-gray-800">{Number(inst.planned_amount).toFixed(2)}</td>
                    <td className="px-3 py-2 text-gray-600">{inst.due_date ? inst.due_date.slice(0, 10) : '—'}</td>
                    <td className="px-3 py-2 text-center">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${inst.status === 'Paid' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                        {inst.status}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      {inst.voucher_id ? <Link to={`/vouchers/${inst.voucher_id}`} className="text-blue-700 hover:underline">{inst.voucher_no}</Link> : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div className="flex gap-3 p-6 pt-0 flex-shrink-0">
          <button type="button" onClick={onClose}
            className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-lg text-sm hover:bg-gray-50">Close</button>
        </div>
      </div>
    </div>
  );
};

const ProgramReports = () => {
  const [dimension, setDimension] = useState('institution');
  const [phases, setPhases] = useState([]);
  const [phaseFilter, setPhaseFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [drilldownInstitution, setDrilldownInstitution] = useState(null);

  useEffect(() => {
    accountsApi.get('/phases').then(res => setPhases(res.data.phases)).catch(() => {});
  }, []);

  const runReport = async () => {
    try {
      setLoading(true);
      const params = { group_by: dimension };
      if (phaseFilter && dimension !== 'phase') params.phase_id = phaseFilter;
      if (statusFilter && dimension === 'institution') params.status = statusFilter;
      const response = await accountsApi.get('/program-reports', { params });
      setReport(response.data.program_report);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load program report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { runReport(); }, [dimension, phaseFilter, statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const rowsWithMath = (report?.rows || []).map(r => {
    const contractValue = Number(r.contract_value);
    const actualPaid = Number(r.actual_paid);
    const remaining = contractValue - actualPaid;
    const pctUtilized = contractValue > 0 ? (actualPaid / contractValue) * 100 : 0;
    return { ...r, contractValue, actualPaid, remaining, pctUtilized };
  });

  const unassignedContractValue = report?.unassigned ? Number(report.unassigned.contract_value ?? 0) : 0;
  const unassignedActualPaid = report?.unassigned ? Number(report.unassigned.actual_paid ?? 0) : 0;
  const unassignedRemaining = unassignedContractValue - unassignedActualPaid;

  const totals = rowsWithMath.reduce((acc, r) => ({
    contractValue: acc.contractValue + r.contractValue,
    actualPaid: acc.actualPaid + r.actualPaid,
  }), { contractValue: 0, actualPaid: 0 });
  totals.contractValue += unassignedContractValue;
  totals.actualPaid += unassignedActualPaid;

  // Institution rows get their own Phase column (in the table and the
  // export) since the same institution code can legitimately repeat across
  // phases — a fresh contract each procurement cycle.
  const colSpan = dimension === 'institution' ? 7 : 6;

  const exportToExcel = () => {
    const dimLabel = DIMENSIONS.find(d => d.key === dimension)?.label || dimension;
    const data = rowsWithMath.map(r => ({
      Code: r.code, Name: r.name,
      ...(dimension === 'institution' ? { Phase: `${r.phase_code} — ${r.phase_name}` } : {}),
      'Contract Value': r.contractValue, 'Actual Paid': r.actualPaid,
      Remaining: r.remaining, '% Utilized': Number(r.pctUtilized.toFixed(1)),
    }));
    if (report?.unassigned) {
      data.push({
        Code: '', Name: report.unassigned.label,
        ...(dimension === 'institution' ? { Phase: '' } : {}),
        'Contract Value': unassignedContractValue, 'Actual Paid': unassignedActualPaid,
        Remaining: unassignedRemaining, '% Utilized': '',
      });
    }
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, dimLabel);
    XLSX.writeFile(wb, `program-report-${dimension}.xlsx`);
  };

  return (
    <Layout title="Program Reports">
      <div className="flex items-center justify-between mb-4">
        <div className="flex gap-2 border-b border-gray-200">
          {DIMENSIONS.map(d => (
            <button key={d.key} onClick={() => setDimension(d.key)}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition ${dimension === d.key ? 'border-blue-800 text-blue-800' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
              {d.label}
            </button>
          ))}
        </div>
        <button onClick={exportToExcel} disabled={!report}
          className="flex items-center gap-2 border border-gray-300 text-gray-700 px-4 py-2 rounded-lg text-sm hover:bg-gray-50 disabled:opacity-50">
          <Download size={16} /> Export
        </button>
      </div>

      {(dimension === 'institution' || dimension === 'district') && (
        <div className="bg-white rounded-xl shadow p-4 mb-4">
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Phase</label>
              <select value={phaseFilter} onChange={(e) => setPhaseFilter(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[16rem]">
                <option value="">All Phases</option>
                {phases.map(p => <option key={p.id} value={p.id}>{p.code} — {p.name}</option>)}
              </select>
            </div>
            {dimension === 'institution' && (
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Payment Status</label>
                <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                  {STATUS_OPTIONS.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
                </select>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl shadow overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="text-left px-4 py-3 text-gray-600 font-semibold">Code</th>
              <th className="text-left px-4 py-3 text-gray-600 font-semibold">Name</th>
              {dimension === 'institution' && <th className="text-left px-4 py-3 text-gray-600 font-semibold">Phase</th>}
              <th className="text-right px-4 py-3 text-gray-600 font-semibold">Contract Value</th>
              <th className="text-right px-4 py-3 text-gray-600 font-semibold">Actual Paid</th>
              <th className="text-right px-4 py-3 text-gray-600 font-semibold">Remaining</th>
              <th className="text-right px-4 py-3 text-gray-600 font-semibold">% Utilized</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={colSpan} className="text-center py-8 text-gray-500">Loading...</td></tr>
            ) : rowsWithMath.length === 0 ? (
              <tr><td colSpan={colSpan} className="text-center py-8 text-gray-500">No data yet</td></tr>
            ) : rowsWithMath.map((r) => (
              <tr key={r.id} className={`border-t border-gray-100 hover:bg-gray-50 ${dimension === 'institution' ? 'cursor-pointer' : ''}`}
                onClick={dimension === 'institution' ? () => setDrilldownInstitution(r) : undefined}>
                <td className="px-4 py-2 font-mono text-gray-700">{r.code}</td>
                <td className="px-4 py-2 text-gray-700">
                  {r.name}
                  {dimension === 'institution' && <ListChecks size={12} className="inline-block ml-1.5 text-gray-400" />}
                </td>
                {dimension === 'institution' && <td className="px-4 py-2 text-gray-600">{r.phase_code} — {r.phase_name}</td>}
                <td className="px-4 py-2 text-right text-gray-800">{r.contractValue.toFixed(2)}</td>
                <td className="px-4 py-2 text-right text-gray-800">{r.actualPaid.toFixed(2)}</td>
                <td className={`px-4 py-2 text-right font-medium ${r.remaining < 0 ? 'text-red-600' : 'text-gray-800'}`}>{r.remaining.toFixed(2)}</td>
                <td className={`px-4 py-2 text-right ${r.pctUtilized > 90 ? 'text-red-600 font-medium' : 'text-gray-600'}`}>{r.pctUtilized.toFixed(1)}%</td>
              </tr>
            ))}
            {!loading && report?.unassigned && (
              <tr className="border-t border-gray-100 bg-gray-50 italic">
                <td className="px-4 py-2 text-gray-500">—</td>
                <td className="px-4 py-2 text-gray-600">{report.unassigned.label}</td>
                {dimension === 'institution' && <td className="px-4 py-2 text-gray-500">—</td>}
                <td className="px-4 py-2 text-right text-gray-600">{unassignedContractValue.toFixed(2)}</td>
                <td className="px-4 py-2 text-right text-gray-600">{unassignedActualPaid.toFixed(2)}</td>
                <td className="px-4 py-2 text-right text-gray-600">{unassignedRemaining.toFixed(2)}</td>
                <td className="px-4 py-2 text-right text-gray-500">—</td>
              </tr>
            )}
          </tbody>
          {!loading && report && (
            <tfoot>
              <tr className="border-t border-gray-200 font-semibold bg-gray-50">
                <td colSpan={dimension === 'institution' ? 3 : 2} className="px-4 py-3 text-right text-gray-600 text-xs">Total</td>
                <td className="px-4 py-3 text-right text-gray-800">{totals.contractValue.toFixed(2)}</td>
                <td className="px-4 py-3 text-right text-gray-800">{totals.actualPaid.toFixed(2)}</td>
                <td className="px-4 py-3 text-right text-gray-800">{(totals.contractValue - totals.actualPaid).toFixed(2)}</td>
                <td></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {drilldownInstitution && (
        <InstallmentDrilldown institution={drilldownInstitution} onClose={() => setDrilldownInstitution(null)} />
      )}
    </Layout>
  );
};

export default ProgramReports;
